import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import PageHeader from '../shell/PageHeader';
import { DataTable, FilterBar, Field, Modal, Menu, MenuItem, Notice } from '../patterns';
import type { DataTableColumn } from '../patterns';
import { listGlobalCropTypes } from '../../services/cropTypeService';
import type { CropTypeNode } from '../../services/cropTypeService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function depthOf(node: CropTypeNode, byCode: Map<string, CropTypeNode>): number {
  let d = 0;
  let cur: string | null = node.parentCode;
  const seen = new Set<string>();
  while (cur && byCode.has(cur) && !seen.has(cur)) {
    seen.add(cur);
    d += 1;
    cur = byCode.get(cur)!.parentCode;
  }
  return d;
}

// ─── CropEditModal ────────────────────────────────────────────────────────────

interface EditModalProps {
  show: boolean;
  node: Partial<CropTypeNode> | null;
  allNodes: CropTypeNode[];
  onHide: () => void;
  onSaved: () => void;
}

const CropEditModal: React.FC<EditModalProps> = ({ show, node, allNodes, onHide, onSaved }) => {
  const { t } = useTranslation();
  const isEditing = !!(node && node._id);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [parentCode, setParentCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCode(node?.code || '');
    setName(node?.name || '');
    setParentCode(node?.parentCode || '');
    setError(null);
  }, [node, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { code: code.toLowerCase(), name, parentCode: parentCode || null };
      if (isEditing && node?._id) {
        await axios.put(`/api/v1/global-crop-types/${node._id}`, payload, { withCredentials: true });
      } else {
        await axios.post('/api/v1/global-crop-types', payload, { withCredentials: true });
      }
      onSaved();
      onHide();
    } catch (err: any) {
      setError(err.response?.data?.error || t('system.crops.errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      title={isEditing ? t('system.crops.modal.editTitle') : t('system.crops.modal.createTitle')}
    >
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Notice tone="danger" className="mb-3">{error}</Notice>}

          <Field label={t('system.crops.modal.fields.code')} controlId="cropEditCode" required>
            <input
              id="cropEditCode"
              type="text"
              className="pat-input"
              value={code}
              required
              disabled={isEditing}
              onChange={e => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              aria-label={t('system.crops.modal.fields.code')}
            />
          </Field>

          <Field label={t('system.crops.modal.fields.name')} controlId="cropEditName" required>
            <input
              id="cropEditName"
              type="text"
              className="pat-input"
              value={name}
              required
              onChange={e => setName(e.target.value)}
              aria-label={t('system.crops.modal.fields.name')}
            />
          </Field>

          <Field label={t('system.crops.modal.fields.parent')} controlId="cropEditParent">
            <select
              id="cropEditParent"
              className="pat-input"
              value={parentCode}
              onChange={e => setParentCode(e.target.value)}
              aria-label={t('system.crops.modal.fields.parent')}
            >
              <option value="">{t('system.crops.modal.parentNone')}</option>
              {allNodes.filter(n => n.code !== code).map(n => (
                <option key={n.code} value={n.code}>{n.name}</option>
              ))}
            </select>
          </Field>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="pat-btn pat-btn--subtle" onClick={onHide}>
            {t('system.crops.modal.cancel')}
          </button>
          <button type="submit" className="pat-btn pat-btn--primary" disabled={loading}>
            {t('system.crops.modal.save')}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

// ─── SystemCropTypesPage ──────────────────────────────────────────────────────

const SystemCropTypesPage: React.FC = () => {
  const { t } = useTranslation();

  useBreadcrumb([
    { label: t('nav.system.module'), to: '/system' },
    { label: t('system.crops.title') },
  ]);

  const [nodes, setNodes] = useState<CropTypeNode[]>([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState<Partial<CropTypeNode> | null>(null);

  const fetchNodes = useCallback(async () => {
    try {
      const data = await listGlobalCropTypes();
      setNodes(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || t('system.crops.errors.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    fetchNodes();
  }, [fetchNodes]);

  const byCode = useMemo(() => new Map(nodes.map(n => [n.code, n])), [nodes]);

  const filtered = useMemo(() => {
    if (!search.trim()) return nodes;
    const q = search.toLowerCase();
    return nodes.filter(n =>
      n.name.toLowerCase().includes(q) || n.code.toLowerCase().includes(q)
    );
  }, [nodes, search]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('system.crops.deleteConfirm'))) return;
    try {
      await axios.delete(`/api/v1/global-crop-types/${id}`, { withCredentials: true });
      await fetchNodes();
    } catch (err: any) {
      setError(err.response?.data?.error || t('system.crops.errors.deleteFailed'));
    }
  };

  const columns: DataTableColumn<CropTypeNode>[] = [
    {
      key: 'name',
      label: t('system.crops.columns.name'),
      sortable: true,
      render: n => (
        <span style={{ paddingLeft: `${depthOf(n, byCode) * 20}px` }}>{n.name}</span>
      ),
    },
    {
      key: 'code',
      label: t('system.crops.columns.code'),
      sortable: true,
      render: n => <code>{n.code}</code>,
    },
    {
      key: 'parentCode',
      label: t('system.crops.columns.parent'),
      render: n => n.parentCode || '—',
    },
    {
      key: 'actions',
      label: t('system.crops.columns.actions'),
      render: n => (
        <Menu
          trigger={
            <button
              type="button"
              className="pat-menu__trigger"
              aria-label={t('system.crops.columns.actions')}
            >
              ⋮
            </button>
          }
          placement="bottom-end"
        >
          <MenuItem onClick={() => { setEditing({ ...n }); setShowEdit(true); }}>
            Edit
          </MenuItem>
          <MenuItem onClick={() => handleDelete(n._id)} tone="danger">
            Delete
          </MenuItem>
        </Menu>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('system.crops.title')}
        meta={t('system.crops.metaCount', { count: nodes.length })}
        actions={
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={() => { setEditing(null); setShowEdit(true); }}
          >
            {t('system.crops.newButton')}
          </button>
        }
      />

      {error && <Notice tone="danger">{error}</Notice>}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('system.crops.searchPlaceholder')}
      />

      <DataTable<CropTypeNode>
        columns={columns}
        rows={filtered}
        rowKey={r => r._id}
        emptyState={t('system.crops.empty')}
      />

      <CropEditModal
        show={showEdit}
        node={editing}
        allNodes={nodes}
        onHide={() => setShowEdit(false)}
        onSaved={fetchNodes}
      />
    </>
  );
};

export default SystemCropTypesPage;
